import { useState, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { Divider, Grid2 as Grid, Typography, Alert, Stack, FormControl, OutlinedInput, InputAdornment, InputLabel, Select, MenuItem, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import { Search } from '@mui/icons-material';
import { useErrorBoundary } from 'react-error-boundary';

import YtdReport from '../../../types/reports/ytdReport';
import YearToDateReportsTable from './YearToDateReportsTable';
import DistrictType from '../../../types/filers/districtType';
import ReportListExportSession from './ReportListExportSection';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';

const OfficeList: DistrictType[] = [
    {
        office: "Governor's Council",
        code: -1,
        description: "Council",
        incumbentName: "",
        officeTypeGroupId: 4
    },
    {
        office: "Clerk of Courts",
        code: -1,
        description: "Clerk of Courts",
        incumbentName: "",
        officeTypeGroupId: 10
    },
    {
        office: "County Commissioner",
        code: -1,
        description: "County Commissioner",
        incumbentName: "",
        officeTypeGroupId: 10
    },
    {
        office: "District Attorney",
        code: -1,
        description: "District Attorney",
        incumbentName: "",
        officeTypeGroupId: 5
    },
    {
        office: "Register of Deeds",
        code: -1,
        description: "Register of Deeds",
        incumbentName: "",
        officeTypeGroupId: 10
    },
    {
        office: "Register of Probate",
        code: -1,
        description: "Register of Probate",
        incumbentName: "",
        officeTypeGroupId: 10
    },
    {
        office: "Sheriff",
        code: -1,
        description: "Sheriff",
        incumbentName: "",
        officeTypeGroupId: 6
    },
    {
        office: "Superior Court",
        code: -1,
        description: "Superior Court",
        incumbentName: "",
        officeTypeGroupId: 10
    },
    {
        office: "Supreme Judicial Clerk of Courts",
        code: -1,
        description: "Supreme Judicial",
        incumbentName: "",
        officeTypeGroupId: 10
    },
    {
        office: "Treasurer",
        code: -1,
        description: "Treasurer",
        incumbentName: "",
        officeTypeGroupId: 10
    },
]

const CountyReports = (): JSX.Element => {
    
    document.title = 'OCPF - County Candidates Reports';

    const { showBoundary } = useErrorBoundary();  
    const dropDownYears = Array.from(new Array(10), (val, index) => new Date().getFullYear() - index);
    const [isLoading, setIsLoading] = useState(true);
    const isFirstLoad = useRef(true);

    const [tableRows, setRows] = useState<YtdReport[]>();
    const [reports, setReports] = useState<YtdReport[]>();
    const [reportsSummary, setReportsSummary] = useState('No reports found.');
    const [reportYear, setReportYear] = useState(new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear());
    const [officeList, setOfficeList] = useState<DistrictType[]>(OfficeList);
    const [showOnBallot, setShowOnBallot] = useState(false);

    const [excelExportUrl, setExcelExportUrl] = useState('');
    const [txtExportUrl, setTxtExportUrl] = useState('');


    useEffect(() => {
        if (isFirstLoad.current) {
            getCountyReportList();
            isFirstLoad.current = false;
        }
        else {
            getCountyReportList();
        }
    }, [reportYear, showOnBallot])

    const getCountyReportList = async () => {

        setExcelExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/county/ytd/excelOutput/${reportYear}?onBallot=${showOnBallot}`);
        setTxtExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/county/ytd/textOutput/${reportYear}?onBallot=${showOnBallot}`);

        setIsLoading(true);

        try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/county/ytd/${reportYear}?onBallot=${showOnBallot}&PageSize=1000`)
                .then(result => {
                    if (!result.ok) {
                        showBoundary(new Error('Failed to get county candidates reports.'));
                    }
                    return result.json();
                })
                .then(result => {
                    let count = result.reports.length;
                    if (count === 0) {
                        setReportsSummary("No reports found.");
                        setRows([]);
                    }
                    else {
                        setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
                        setRows(result.reports);
                        setReports(result.reports);
                    }
                    setIsLoading(false);
                });
        } catch (error) {
            console.error('Uncaught Error:', error);
            showBoundary(new Error(`${error}`));
        }
    }

    const updateReporYear = (event: any) => {
        setReportYear(event.target.value);
    };

    const filterByName = debounce((event: any) => {
        let searchInput = event.target.value.toLowerCase();
        if (searchInput.length === 0) {
            let count = reports ? reports.length : 0;
            setRows(reports);
            setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
        }
        else {
            let filteredRows = reports?.filter((row) => row.filerName.toLowerCase().includes(searchInput)) || [];
            let count = filteredRows.length;
            setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
            setRows(filteredRows);
        }
    }, 800);

    const filterByOffice = (event: any) => {
        let officeCode = event.target.value;
        if (officeCode === "0") {
            let count = reports ? reports.length : 0;
            setRows(reports);
            setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
        }
        else {
            let filteredRows = reports?.filter((row: YtdReport) => row.officeSought.includes(officeCode)) || [];
            let count = filteredRows.length;
            setReportsSummary(`Viewing 1 through ${count} of ${count} reports`);
            setRows(filteredRows);
        }
    }

    const showOnBallotOnly = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowOnBallot(event.target.checked);
    };


    return (
        <Main>
            <Container>
                <PageBreadCrumbs></PageBreadCrumbs>
                <Typography
                    variant="h5"
                    align={'left'}
                    data-aos={'fade-up'}
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                    }}
                    paddingTop={3}
                    marginBottom={2}
                >
                    County / District Candidates Depository Year-to-Date Reports
                </Typography>
                <Divider />
                <Grid container marginBottom={2} marginTop={3}>
                    <Grid size={{ xs: 12, md: 10 }}>
                        <Stack direction={"row"} spacing={1}
                            sx={{ mb: 1 }}
                        >
                            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                <InputLabel id="year-id">Year</InputLabel>
                                <Select
                                    sx={{ fontSize: 'smaller' }}
                                    labelId="year-id"
                                    id="year-select"
                                    aria-labelledby='year-id'
                                    label="Report Year"
                                    defaultValue={reportYear}
                                    onChange={updateReporYear}
                                >
                                    {
                                        dropDownYears.map((year) =>
                                            <MenuItem value={year} key={year} >{year}</MenuItem>
                                        )
                                    }
                                </Select>
                            </FormControl>
                            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                                <InputLabel id="office-id">Filter by Office</InputLabel>
                                <Select
                                    sx={{ fontSize: 'smaller' }}
                                    labelId="office-id"
                                    id="office-select"
                                    aria-labelledby='office-id'
                                    label="Filter by Office"
                                    defaultValue={"0"}
                                    onChange={filterByOffice}
                                >
                                    <MenuItem value="0" sx={{ fontSize: "small" }}>All County Offices</MenuItem>
                                    {
                                        officeList?.map((office: DistrictType) =>
                                            <MenuItem value={office.description} key={office.description} sx={{ fontSize: "small" }}>{office.office}</MenuItem>
                                        )
                                    }
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ m: 1, p: 0, maxWidth: 200 }}>
                                <OutlinedInput
                                    aria-label='Filter by filer name'
                                    placeholder={"Filter by filer name"}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    }
                                    onChange={filterByName}
                                    sx={{ fontSize: 'smaller' }}
                                />

                            </FormControl>
                              <FormControl size='small'>
                                <FormControlLabel control={<Checkbox checked={showOnBallot} onChange={showOnBallotOnly} inputProps={{ 'aria-label': 'show on ballot' }} />} label="Show on ballot only?" sx={{fontSize: "smaller"}} />
                            </FormControl>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                        <ReportListExportSession excelExportUrl={excelExportUrl} txtExportUrl={txtExportUrl}></ReportListExportSession>
                    </Grid>
                    <Grid size={11.5}>
                        <Alert icon={false} severity="info"
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                '.MuiAlert-message': {
                                    padding: '4px',
                                }
                            }}
                        >
                            {reportsSummary}
                        </Alert>
                    </Grid>
                </Grid>
                {/*report table*/}
                <YearToDateReportsTable tableRows={tableRows} isCandidate={true}></YearToDateReportsTable>
                {/*loading indicator*/}
                <CircularProgress
                aria-label='loading'
                    size={50}
                    className={isLoading ? "" : "hidden"}
                    sx={{
                        position: "fixed",
                        left: "50%",
                        top: "20%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 2
                    }}
                />
            </Container>
        </Main>
    );
};

export default CountyReports;
